import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';
    
function PhenytoinCalculator({user}) {
    const [albumin, setAlbumin] = useState ('');
    const [measuredLevel, setMeasuredLevel] = useState ('');
    const [isEsrf, setIsEsrf] = useState ('');
    const [albUnit, setAlbUnit] = useState ('g/L');
    const [weight, setWeight] = useState ('');
    const [currentDose, setCurrentDose] = useState ('');
    const [targetLevel, setTargetLevel] = useState ('');
    // Step 1: Population Constants
    const VMAX = 7; 
    const KM = 4;
    const S = 0.92; // Phenytoin Sodium Capsules
    const F = 1.0;

    // Step 2: Albumin Correction (Sheiner-Tozer)
    const albuminNum = parseFloat(albumin) || 0;
    // Normalizing to g/L (if unit is g/dL, multiply by 10)
    const albG_L = albUnit === 'g/dL' ? albuminNum * 10 : albuminNum;

    // Step 1: Logic inside your component
    const multiplier = isEsrf ? (0.48*0.9) : 0.9;
    const constant = 0.1;

    // Use the formula from your handbook
    const albDenominator = (multiplier * (albG_L / 44)) + constant;
    const correctedLevel = albDenominator > 0 ? measuredLevel / albDenominator : 0;
    // Step 3: Maintenance Dose Calculation (Michaelis-Menten)
    // Dose = (Vmax * Css) / (Km + Css)
    const weightNum = parseFloat(weight) || 0;
    const currentDoseNum = parseFloat(currentDose) || 0; // The dose they are taking now
    // Step 2 Logic: Predict Css from Current Dose
    // Formula: (Km * S * F * Dose) / ((Vmax * BW) - (S * F * Dose))
    const numerator = KM * S * F * currentDoseNum;
    const cssDenominator = (VMAX * weightNum) - (S * F * currentDoseNum);

    const predictedCss = cssDenominator > 0 ? numerator / cssDenominator : 0;

    const percentDev = ((correctedLevel - predictedCss) / predictedCss) * 100;

    let comparisonResult = {
        text: "Within Range",
        color: "#2ecc71",
        note: "Patient is responding as expected for an average adult/pediatric profile."
    };

    if (correctedLevel < predictedCss) {
        comparisonResult = {
            text: "Below Predicted Range",
            color: "#3498db",
            note: `Result is ${Math.abs(percentDev).toFixed(0)}% lower than expected. Check compliance or induction.`
        };
    } else if (correctedLevel > predictedCss) {
        comparisonResult = {
            text: "Above Predicted Range",
            color: "#e67e22",
            note: `Result is ${percentDev.toFixed(0)}% higher than expected. Check for slow metabolism or inhibition.`
        };
    }
    // Step 3 Logic: Refined Clinical Suggestion
    let suggestion = "";
    let suggestionColor = "#2ecc71"; 

    if (Number(correctedLevel) >= 10 && Number(correctedLevel) <= 20) {
        suggestion = "Level within therapeutic range (10-20 mcg/mL). If response is poor but compliance good, consider a small dose increment (do not exceed max). If response is good, continue current dose.";
        suggestionColor = "#2ecc71"; // Green
    } else if (Number(correctedLevel) < 10) {
        suggestion = "Level is sub-therapeutic (< 10 mcg/mL). If response is poor, Check compliance first before consider a dose increment. If response is poor but compliance is good, increase dose and re-sample. If response is good, continue current dose.";
        suggestionColor = "#f1c40f"; // Yellow
    } else if (Number(correctedLevel) > 20) {
        suggestion = "Level is TOXIC (> 20 mcg/mL). Withhold dose and monitor for clinical signs of toxicity (e.g., Hypotension, Excessive sedation, Respiratory depression). Review dose once level falls.";
        suggestionColor = "#e74c3c"; // Red
    }
    
    // Add this logic before the return
    const getClinicalInsight = () => {
        if (!correctedLevel || !predictedCss) return null;
        
        const actual = Number(correctedLevel);
        const estimate = Number(predictedCss);
        
        if (actual > estimate * 1.2) { // More than 20% higher than estimate
            return {
                title: "Actual > Population Estimate",
                reason: "Patient may be a 'Slow Metabolizer' or taking an inhibitor (e.g.,Allopurinol, Amiodarone, Azole derivatives, Calcium Channel Blocker, Carbamazepine, Cefazolin, Chloramphenicol, Fluoxetine, Fluvoxamine, Hydroxyzine, Isoniazid, Proton Pump Inhibitors, Serotonin Reuptake Inhibitors, Sulfonamide Derivatives, Tacrolimus, Ticlopidine, Topiramate, Trimethoprim, Vitamin K Antagonist, Valproate).",
                color: "#e67e22"
            };
        } else if (actual < estimate * 0.8) { // More than 20% lower than estimate
            return {
                title: "Actual < Population Estimate",
                reason: "Patient may be a 'Fast Metabolizer', have poor absorption, Albumin DISPLACEMENT or a compliance issue. Also consider induction by other drugs (e.g. Carbamazepine, Ciprofloxacin, Folic Acid, Methotrexate, Pyridoxine, Rifampin, Ritonavir, Theophylline, Valproic Acid).",
                color: "#3498db"
            };
        }
        return {
            title: "Matches Population Estimate",
            reason: "Patient's metabolism is consistent with standard population parameters.",
            color: "#2ecc71"
        };
    };
    const insight = getClinicalInsight();

    // Step 2 Logic: Calculate Dose for Target Css
    const targetNum = parseFloat(targetLevel) || 0; // Default target 15 mcg/mL

    // Formula: (Vmax * BW * Target) / (S * F * (Km + Target))
    const doseNumerator = VMAX * weightNum * targetNum;
    const doseDenominator = S * F * (KM + targetNum);

    const suggestedDose = doseDenominator > 0 ? doseNumerator / doseDenominator : 0;
    
    const [targetWithhold, setTargetWithhold] = useState(15);
    const VD_CONSTANT = 0.7; // L/kg

    const vmTotal = VMAX * weightNum; 
    const vdTotal = VD_CONSTANT * weightNum;

    const calculateTimeWithhold = () => {
        if (correctedLevel <= targetWithhold || vmTotal === 0) return 0;
        // Handbook Formula Implementation
        const term1 = KM * Math.log(correctedLevel / targetWithhold);
        const term2 = (correctedLevel - targetWithhold);
        return (term1 + term2) * (vdTotal / vmTotal); // Days
    };

    const timeDays = calculateTimeWithhold();

    return (
    
    <div style={{ padding: '20px', background: '#1e272e', borderRadius: '10px', marginTop: '20px', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button 
                type="button"
                className="no-print" 
                onClick={() => {
                                const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                                const trackingLabel = `${actualUserEmail}-printed-Phenytoin`;
                                logUserActivityQuietly(trackingLabel); 
                                window.print();
                            }} 
                style={{
                padding: '10px 20px',
                backgroundColor: '#27ae60', 
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                }}
            >
                🖨️ Print PDF Report
            </button>
        </div>
        <h2 style = {{textAlign: 'center', color: '#3498db'}}>Phenytoin TDM Worksheet</h2>
        <details style={{
            backgroundColor: '#1E293B',
            border: '1px solid #38BDF8',
            borderRadius: '8px',
            padding: '10px 15px',
            marginBottom: '20px',
            cursor: 'pointer'
            }}>
            <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
                ⏱️ Check Clinical Sampling Protocol (Click to expand)
            </summary>
            <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                {/* SECTION 1: SAMPLING TIMELINE MATRIX TABLE */}
                <div style={{ marginBottom: '15px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                        ⏱️ Loading & Maintenance Sampling Windows
                    </span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff', width: '35%' }}>After LD (IV):</td>
                                <td style={{ padding: '8px 0', color: '#bdc3c7' }}>Draw exactly **2 hours** following the complete end of infusion.</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff' }}>After LD (Oral):</td>
                                <td style={{ padding: '8px 0', color: '#bdc3c7' }}>Draw exactly **24 hours** after the ingestion of the load.</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff', verticalAlign: 'top' }}>No Loading Dose:</td>
                                <td style={{ padding: '8px 0', color: '#bdc3c7', lineHeight: '1.4' }}>
                                    Draw **7 – 10 days** after initiating therapy or implementing a change in the daily maintenance regimen.
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#fff' }}>Routine Pre-Sample:</td>
                                <td style={{ padding: '8px 0', color: '#bdc3c7' }}>Draw a true trough level **just before** the next scheduled dose.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* SECTION 2: TARGET GRID & PROTOCOL ACTION */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    {/* Therapeutic Target Card */}
                    <div style={{ background: 'rgba(46, 204, 113, 0.06)', border: '1px solid rgba(46, 204, 113, 0.2)', padding: '10px', borderRadius: '6px' }}>
                        <div style={{ fontWeight: 'bold', color: '#2ecc71', fontSize: '0.8rem', textTransform: 'uppercase' }}>🎯 Therapeutic Range:</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginTop: '4px', color: '#fff' }}>
                            10 - 20 <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg/L</span>
                        </div>
                    </div>

                    {/* Reload Calculation Protocol Action Card */}
                    <div style={{ background: 'rgba(155, 89, 182, 0.08)', border: '1px solid rgba(155, 89, 182, 0.2)', padding: '10px', borderRadius: '6px' }}>
                        <div style={{ fontWeight: 'bold', color: '#9b59b6', fontSize: '0.8rem', textTransform: 'uppercase' }}>💊 Reloading Protocol:</div>
                        <div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#ecf0f1', lineHeight: '1.3' }}>
                            Use the **1st concentration after LD** to back-calculate the exact dose needed for a reload.
                        </div>
                    </div>
                </div>

                {/* SECTION 3: CRITICAL SAFETY TOXICITY WARNING */}
                <div style={{ background: 'rgba(231, 76, 60, 0.08)', borderLeft: '4px solid #e74c3c', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c', textTransform: 'uppercase', display: 'block' }}>
                        ⚠️ Toxicity Threshold Warning
                    </span>
                    <div style={{ fontSize: '0.85rem', color: '#ecf0f1', marginTop: '4px' }}>
                        Concentrations <strong>&gt; 20 mg/L</strong> cause progressive CNS toxicity (starting with horizontal nystagmus, progressing to ataxia, slurred speech, and lethargy).
                    </div>
                </div>

            </div>
        </details>
        
        {/* STEP 1: ALBUMIN CORRECTION */}
        <section style={{ marginBottom: '20px', borderBottom: '1px solid #34495e', paddingBottom: '15px' }}>
            <h3 style={{ color: '#3498db' }}>Step 0: Correction</h3>

            <label>Body Weight (kg):</label>
            <div>
                <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} style={inputStyle} />
            </div>

            <label>Lab Measured Level (mcg/mL):</label>
            <input type="number" value={measuredLevel} onChange={(e) => setMeasuredLevel(e.target.value)} style={inputStyle} />
            
            <label style={{ display: 'block', marginTop: '10px' }}>Albumin ({albUnit}):</label>
            <div style={{ display: 'flex', gap: '5px' }}>
                <input type="number" value={albumin} onChange={(e) => setAlbumin(e.target.value)} style={inputStyle} />
                <button onClick={() => setAlbUnit(albUnit === 'g/dL' ? 'g/L' : 'g/dL')} style={btnStyle}>{albUnit}</button>
            </div>
            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input 
                    type="checkbox" 
                    id="esrfToggle"
                    checked={isEsrf} 
                    onChange={(e) => setIsEsrf(e.target.checked)} 
                    style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label htmlFor="esrfToggle" style={{ color: '#ecf0f1', cursor: 'pointer', fontSize: '0.9rem' }}>
                    Patient has ESRF / Poor Renal Function (CrCl &lt; 10 mL/min)?
                </label>
            </div>
            {/* Transparency Formula Display */}
            <div style={{ 
                marginTop: '15px', 
                padding: '12px', 
                background: '#0a3d62', 
                borderRadius: '6px', 
                borderLeft: '4px solid #3498db' 
            }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', color: '#bdc3c7', fontWeight: 'bold' }}>
                    Step 1: Normal Binding Correction (Sheiner-Tozer)
                </p>
                
                <div style={{ textAlign: 'center', padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '4px' }}>
                    {/* Math Structure */}
                    <div style={{ display: 'inline-block', verticalAlign: 'middle', textAlign: 'right', paddingRight: '10px' }}>
                        Corrected Level =
                    </div>
                    <div style={{ display: 'inline-block', verticalAlign: 'middle', textAlign: 'center' }}>
                        <div style={{ borderBottom: '1px solid white', paddingBottom: '2px' }}>
                            Measured Level ({measuredLevel || 0})
                        </div>
                        <div style={{ paddingTop: '2px' }}>
                            [ 0.9 × ({albG_L} / 44) ] + 0.1
                        </div>
                    </div>
                </div>
                
                <div style={{ paddingTop: '2px' }}>
                    {/* This will now show (0.48)(0.9) when the ESRF toggle is ON */}
                    {isEsrf ? '(0.48)(0.9)' : '0.9'} × ({albG_L} / 44) + 0.1
                </div>
                <p style={{ margin: '10px 0 0 0', fontSize: '0.75rem', color: '#95a5a6', fontStyle: 'italic' }}>
                    *Used when albumin is low (&lt;25 g/L) to estimate free fraction.
                </p>
            </div>
            <div style={{ marginTop: '15px', padding: '10px', background: '#2c3e50', borderRadius: '5px' }}>
                <strong>Corrected Lab Measured Level: </strong>
                <span style={{ color: '#2ecc71', fontSize: '1.2rem' }}>
                    {/* The Number().toFixed() safety net */}
                    {Number(correctedLevel || 0).toFixed(1)} mcg/mL
                </span>
            </div>
        </section>

        {/* STEP 2: DOSING */}
        <section>
            <h3 style={{ color: '#3498db' }}>Step 2: Predict Steady State (Cₚₛₛ)</h3>
            
            <label style={{ display: 'block', marginTop: '10px' }}>Current Daily Dose (mg/day):</label>
            <input type="number" value={currentDose} onChange={(e) => setCurrentDose(e.target.value)} style={inputStyle} />

            <div style={{ marginTop: '15px', padding: '15px', background: '#09123e', borderRadius: '5px', borderLeft: '5px solid #f1c40f' }}>
                <strong>Predicted Steady State (Cₚₛₛ):</strong>
                <h1 style={{ margin: '5px 0', color: '#f18b0f' }}>
                    {Number(predictedCss).toFixed(1)} mcg/mL
                </h1>
                
                {/* Transparency Formula display from your handbook */}
                <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '10px', fontFamily: 'monospace' }}>
                    <div style={{ borderBottom: '1px solid white', display: 'inline-block' }}>
                        {KM} × {S} × {currentDoseNum}
                    </div>
                    <br />
                    ({VMAX} × {weightNum}) - ({S} × {currentDoseNum})
                </div>
            </div>

            {/* Clinical Safety Alert */}
            {predictedCss > 20 && (
                <div style={{ marginTop: '10px', color: '#e74c3c', fontWeight: 'bold', fontSize: '0.8rem' }}>
                    ⚠️ Warning: Predicted level is toxic (&gt20 mcg/mL).
                </div>
            )}
        </section>
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '6px', marginBottom: '15px' }}>
            <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#bdc3c7' }}>Current Status:</p>
            <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                Corrected Level: <span style={{ color: suggestionColor }}>{Number(correctedLevel).toFixed(1)} mcg/mL</span>
            </div>
            <div>
                Predicted Steady State (Cₚₛₛ): <span style={{color: '#f18b0f'}}>{Number(predictedCss).toFixed(1)} mcg/mL</span>
            </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
            <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #34495e' }}>
                <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>PREDICTED RANGE</div>
                <div style={{ color: 'white', fontWeight: 'bold' }}>{Number(predictedCss).toFixed(1)}</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>ACTUAL LEVEL</div>
                <div style={{ fontWeight: 'bold', color: comparisonResult.color }}>{correctedLevel.toFixed(1)}</div>
            </div>
        </div>
        
        <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px', borderLeft: `4px solid ${comparisonResult.color}` , marginTop: '20px'}}>
            <strong style={{ color: comparisonResult.color }}>{comparisonResult.text}</strong>
            <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#bdc3c7' }}>{comparisonResult.note}</p>
        </div>
        
        <section style={{ marginTop: '20px', padding: '15px', background: '#0a3d62', borderRadius: '8px' }}>
            <h3 style={{ color: '#3498db', marginTop: 0 }}>Step 3: Simulation Dose Forecast</h3>
            
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Target Css (mcg/mL):</label>
                    <input type="number" value={targetLevel} onChange={(e) => setTargetLevel(e.target.value)} style={inputStyle} />
                </div>
            </div>

            <div style={{ padding: '15px', background: '#1e272e', borderRadius: '5px', borderLeft: '5px solid #2ecc71' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#bdc3c7' }}>Calculated Maintenance Dose:</p>
                <h1 style={{ margin: '5px 0', color: '#2ecc71' }}>
                    {Math.round(suggestedDose)} <span style={{ fontSize: '1rem' }}>mg/day</span>
                </h1>
                
                {/* Transparency Formula from handbook */}
                <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '10px', fontFamily: 'monospace' }}>
                    Formula: ({VMAX} × {weightNum} × {targetNum}) / ({S} × ({KM} + {targetNum}))
                </div>
            </div>

            <p style={{ fontSize: '0.8rem', color: '#f1c40f', marginTop: '10px' }}>
                💡 <strong>Pharmacy Note:</strong> Rounds to <strong>{Math.round(suggestedDose / 10) * 10} mg</strong> for practical dispensing.
            </p>
        </section>
        <div style={{ 
            marginTop: '25px', 
            padding: '20px', 
            background: '#1e272e', 
            borderRadius: '10px', 
            border: `2px solid ${suggestionColor}`,
            boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
        }}>
            <h4 style={{ margin: '0 0 12px 0', color: suggestionColor, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Step 4:📋</span> Clinical Recommendation
            </h4>

            <div style={{ 
                fontSize: '1.05rem', 
                lineHeight: '1.5', 
                color: '#ecf0f1', 
                fontWeight: '500',
                paddingLeft: '10px',
                borderLeft: `4px solid ${suggestionColor}`
            }}>
                {suggestion}
            </div>
            {insight && (
                <div style={{ 
                    marginTop: '15px', 
                    padding: '12px', 
                    background: '#1b1f23', 
                    borderRadius: '8px', 
                    borderLeft: `5px solid ${insight.color}` 
                }}>
                    <h5 style={{ margin: '0 0 5px 0', color: insight.color, fontSize: '0.9rem' }}>
                        🔍 Interpretation: {insight.title}
                    </h5>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                        {insight.reason}
                    </p>
                </div>
            )}
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #34495e', fontSize: '0.8rem', color: '#95a5a6' }}>
                <strong>Note:</strong> Always correlate with seizure frequency and neurological symptoms. Phenytoin kinetics are non-linear; use caution with any dose changes.
            </div>
        </div>
        <div style={{ marginTop: '25px', padding: '20px', background: '#1b1f23', borderRadius: '10px', border: '1px solid #ff7675' }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#ff7675' }}>⏱️ Time to Withhold Therapy</h4>

            {/* Formula Display */}
            <div style={{ textAlign: 'center', background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '0.8rem', color: '#bdc3c7' }}>Handbook Equation:</p>
                <div style={{ fontSize: '0.9rem', fontFamily: 'monospace', color: '#fff' }}>
                    T = [ {KM} × ln({Number(correctedLevel).toFixed(1)} / {targetWithhold}) + ({Number(correctedLevel).toFixed(1)} - {targetWithhold}) ] × ({vdTotal.toFixed(1)} / {vmTotal.toFixed(1)})
                </div>
            </div>

            {/* Range Slider (Line Dragging) */}
            <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontSize: '0.9rem' }}>
                    Adjust Desired Target: <strong style={{ color: '#ff7675', fontSize: '1.1rem' }}>{targetWithhold} mcg/mL</strong>
                </label>
                <input 
                    type="range" 
                    min="10" 
                    max="20" 
                    step="1" 
                    value={targetWithhold} 
                    onChange={(e) => setTargetWithhold(Number(e.target.value))}
                    style={{ width: '100%', cursor: 'pointer', accentColor: '#ff7675' }} 
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#95a5a6', marginTop: '5px' }}>
                    <span>10 (Aggressive)</span>
                    <span>15 (Standard)</span>
                    <span>20 (Conservative)</span>
                </div>
            </div>

            {/* Result Display */}
            <div style={{ textAlign: 'center', borderTop: '1px solid #34495e', paddingTop: '15px' }}>
                {correctedLevel > targetWithhold ? (
                    <>
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff7675' }}>
                            {(timeDays * 24).toFixed(1)} <span style={{ fontSize: '1rem' }}>Hours</span>
                        </div>
                        <p style={{ margin: '5px 0 0 0', color: '#bdc3c7' }}>Approx. {timeDays.toFixed(1)} Days</p>
                    </>
                ) : (
                    <div style={{ color: '#2ecc71', fontWeight: 'bold' }}>Level already below target.</div>
                )}
            </div>
            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
              <p style={{ margin: '12px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
              </p>
        </div>
    </div>
    );
}
const inputStyle = {
    width: '80%',
    padding: '8px',
    marginTop: '5px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    background: 'white',
    color: 'black'
};

const btnStyle = {
    padding: '0 10px',
    background: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
};

export default PhenytoinCalculator;